import { useCallback, useState } from "react";

export const ORG_TREE_ZOOM_MIN = 0.5;
export const ORG_TREE_ZOOM_MAX = 1.2;
const ZOOM_STEP = 0.1;

// Drives the Current/Proposed OrgTreeCard pair from one shared viewport so
// zooming or panning either side moves both in lockstep — two independently
// scrolled/zoomed trees made visually comparing "what changed" nearly
// impossible once either side drifted out of alignment.
//
// Zoom is a single shared number (both cards render at the same %). Scroll
// is synced as a *fraction* of each card's own scrollable range, not a raw
// pixel offset — the Current and Proposed trees are rarely the same size
// (added/removed roles), so mirroring fractions keeps both views pointed at
// the same relative position in their own tree instead of clipping one side
// against the other's absolute pixel offset. `token` is bumped on every
// user-driven scroll so a card can tell "a new sync arrived" apart from
// "this object was recreated but nothing actually changed".
//
// "At default" (the Reset button's disabled state) is re-derived from the
// live zoom value plus whichever card last reported a scroll telling us
// whether IT is currently back at its own centered default (see
// OrgTreeCard's handleScroll) — not a one-way "has this ever been touched"
// flag, so zooming or scrolling back to exactly the default re-disables
// Reset instead of leaving it stuck enabled.
const useOrgTreeViewportSync = () => {
  const [zoom, setZoom] = useState(1);
  const [hasScrolledAway, setHasScrolledAway] = useState(false);
  const [scrollSync, setScrollSync] = useState({ xFrac: null, yFrac: null, token: 0 });
  const [resetToken, setResetToken] = useState(0);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ORG_TREE_ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ORG_TREE_ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  }, []);

  // Resets shared zoom to 100%, clears the scroll-sync channel (so a late-
  // arriving scroll event from before the reset can't re-broadcast a stale
  // pre-reset position), and asks both cards (via resetToken) to re-center
  // their own scroll position — each card measures its own default center,
  // so this can't just set one shared scroll value.
  const resetView = useCallback(() => {
    setZoom(1);
    setHasScrolledAway(false);
    setScrollSync((s) => ({ xFrac: null, yFrac: null, token: s.token + 1 }));
    setResetToken((t) => t + 1);
  }, []);

  // Called by whichever card the user just scrolled/panned — broadcasts the
  // new position (as a fraction of that card's own scrollable range) to the
  // other card, plus whether that card is currently back at its own centered
  // default (used only to drive the Reset button's disabled state).
  // `sourceVariant` (the reporting OrgTreeCard's own `variant` prop) rides
  // along so that same card can skip re-applying this sync back onto itself
  // — see OrgTreeCard's sync-apply effect for why that self-echo was making
  // both scrollbars jitter.
  const reportUserScroll = useCallback((xFrac, yFrac, atDefault, sourceVariant) => {
    setScrollSync((s) => ({ xFrac, yFrac, token: s.token + 1, sourceVariant }));
    setHasScrolledAway(!atDefault);
  }, []);

  return {
    zoom,
    zoomIn,
    zoomOut,
    resetView,
    isAtDefaultView: zoom === 1 && !hasScrolledAway,
    scrollSync,
    reportUserScroll,
    resetToken,
    zoomMin: ORG_TREE_ZOOM_MIN,
    zoomMax: ORG_TREE_ZOOM_MAX,
  };
};

export default useOrgTreeViewportSync;
